/**
 * The one encoding of the thousands-separator display policy (bug 535512): a separator is drawn
 * after every third-from-last digit of a number's integer part, so only whole-number runs of four
 * or more digits gain any (9,999 / 80,000 / 1,234,567). Returns the 0-based indexes of the digits
 * a separator follows. Callers keep their own drawing mechanism — a text rewrite
 * (groupThousandsInText, for KaTeX HTML and for math read as text), a class on MathLive digit spans
 * (MathLiveField/groupDigits), a width count (estimateLatexWidthInChars) — and share only this arithmetic.
 */
export function thousandsSeparatorPositions(digitCount: number): number[] {
  const positions: number[] = [];
  for (let i = (digitCount - 1) % 3; i < digitCount - 1; i += 3) {
    positions.push(i);
  }
  return positions;
}

// A run of digits not preceded by a digit or a decimal point (decimal parts stay ungrouped).
// Matched with a leading captured group rather than a lookbehind (`(?<![\d.])`), which the
// project's es2015 target / older Safari cannot parse and would throw a SyntaxError on at
// module load.
const NUMBER_RUN = /(^|[^\d.])(\d+)/g;

function groupDigits(digits: string): string {
  const positions = thousandsSeparatorPositions(digits.length);
  if (positions.length === 0) return digits;
  let grouped = '';
  let next = 0;
  for (const position of positions) {
    grouped += `${digits.slice(next, position + 1)},`;
    next = position + 1;
  }
  return grouped + digits.slice(next);
}

/** Every whole number of four or more digits in `text`, written grouped (`80000 cm` reads `80,000 cm`). */
export function groupThousandsInText(text: string): string {
  return text.replace(NUMBER_RUN, (_, prefix: string, digits: string) => prefix + groupDigits(digits));
}
